import React from "react";

import {
  AlertTriangle,
  ArrowDownRight,
  ArrowRight,
  ArrowUpRight,
  HelpCircle,
  Lightbulb,
  ListChecks,
  Loader2,
  type LucideIcon,
} from "lucide-react";

import {
  ACCENT,
  CollapsibleSection,
  GroupCard,
  MoveList,
  MUTED,
  PointList,
  QuoteBand,
  ScoreStrip,
} from "./Chrome";
import { formatDate } from "../lib/derive";
import {
  buildKpiExecBrief,
  type KpiExecBriefKeyNumber,
} from "../lib/deriveKpiExecBrief";

/**
 * KPI Metrics Executive Brief — the L2 read behind the KPI Situation Card,
 * laid out as xfactor's bubble-chart-kpi/exec-brief-modal.tsx: Situation at
 * a glance, then Must Know (KPI landscape, performance health), Watch-Outs
 * (what's changing, landmines, relationship implications), Recommended
 * Actions and Owners, and Open questions.
 */

const ANCHORS: Array<{ id: string; label: string; icon: LucideIcon }> = [
  { id: "keb-must-know", label: "Must know", icon: Lightbulb },
  { id: "keb-watch-outs", label: "Watch-outs", icon: AlertTriangle },
  { id: "keb-playbook", label: "Recommended actions", icon: ListChecks },
  { id: "keb-questions", label: "Open questions", icon: HelpCircle },
];

const TREND_TONE: Record<string, string> = {
  improving: "bg-green-50 text-green-700",
  stable: "bg-amber-50 text-amber-700",
  deteriorating: "bg-red-50 text-red-700",
  unclear: "bg-gray-100 text-gray-600",
};

const COVERAGE_TONE: Record<string, string> = {
  "well measured": "bg-green-50 text-green-700",
  "partially measured": "bg-amber-50 text-amber-700",
  sparse: "bg-orange-50 text-orange-700",
  "not measured": "bg-gray-100 text-gray-600",
};

const URGENCY_LABEL: Record<string, string> = {
  now: "Now",
  "30d": "30 days",
  quarter: "This quarter",
};

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const Chip: React.FC<{ tone?: string; children: React.ReactNode }> = ({
  tone = "bg-gray-100 text-gray-600",
  children,
}) => (
  <span
    className={`rounded px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${tone}`}
  >
    {children}
  </span>
);

const Prose: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-sm leading-relaxed text-gray-700">{children}</p>
);

const Labelled: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <p className="mt-2 text-sm leading-relaxed text-gray-700">
    <span className="font-semibold text-gray-900">{label}: </span>
    {children}
  </p>
);

const DirectionIcon: React.FC<{
  direction: KpiExecBriefKeyNumber["direction"];
}> = ({ direction }) => {
  if (direction === "up")
    return (
      <ArrowUpRight size={14} className="text-green-600" aria-label="Up" />
    );
  if (direction === "down")
    return (
      <ArrowDownRight size={14} className="text-red-600" aria-label="Down" />
    );
  if (direction === "flat")
    return <ArrowRight size={14} className="text-gray-400" aria-label="Flat" />;
  return null;
};

const KpiExecutiveBrief: React.FC<{
  data?: unknown;
  /** While true, show a spinner — do not treat null data as "no brief yet". */
  loading?: boolean;
}> = ({ data, loading = false }) => {
  const brief = React.useMemo(() => buildKpiExecBrief(data), [data]);

  const jumpTo = (id: string) =>
    document
      .getElementById(id)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Loading Executive Brief…
      </div>
    );
  }

  if (!brief.present) {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        <span className="font-medium">
          This briefing has no Executive Brief yet.
        </span>{" "}
        It is written when the KPI Metrics Briefing is generated, so briefings
        generated before it existed need to be regenerated.
      </div>
    );
  }

  return (
    <div>
      <nav className="mb-2 flex flex-wrap gap-2" aria-label="Brief sections">
        {ANCHORS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => jumpTo(id)}
            className="flex items-center gap-1.5 rounded-full border border-[#e3e8f0] bg-white px-3 py-1 text-xs font-medium text-gray-700 transition-colors hover:border-[#ad46ff] hover:text-[#ad46ff]"
          >
            <Icon size={13} aria-hidden />
            {label}
          </button>
        ))}
      </nav>

      <CollapsibleSection title="Situation at a glance" id="keb-glance">
        {brief.asOf ? (
          <div className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            <span className="rounded bg-gray-100 px-2 py-0.5">
              As of: {formatDate(brief.asOf) || brief.asOf}
            </span>
          </div>
        ) : null}
        <ScoreStrip tiles={brief.tiles} />
        {brief.headline ? (
          <div className="mt-4">
            <QuoteBand>{brief.headline}</QuoteBand>
          </div>
        ) : null}
        {brief.whyTheseScores ? (
          <div className="mt-3">
            <Prose>{brief.whyTheseScores}</Prose>
          </div>
        ) : null}
      </CollapsibleSection>

      <GroupCard
        title="Must know"
        Icon={Lightbulb}
        tone="accent"
        id="keb-must-know"
      >
        <CollapsibleSection title="KPI landscape" nested>
          {brief.landscapeText ? <Prose>{brief.landscapeText}</Prose> : null}
          {brief.scorecard.length ? (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wide text-gray-500">
                    <th className="py-1 pr-3 font-semibold">Category</th>
                    <th className="py-1 pr-3 font-semibold">Score</th>
                    <th className="py-1 pr-3 font-semibold">Trend</th>
                    <th className="py-1 font-semibold">Read-through</th>
                  </tr>
                </thead>
                <tbody>
                  {brief.scorecard.map(row => (
                    <tr
                      key={row.category}
                      className="border-t border-[#e3e8f0] align-top"
                    >
                      <td className="py-2 pr-3 font-medium text-gray-900">
                        {row.category}
                      </td>
                      <td className="py-2 pr-3 tabular-nums text-gray-900">
                        {row.score != null ? Math.round(row.score) : "—"}
                      </td>
                      <td className="py-2 pr-3">
                        {row.trend ? (
                          <Chip tone={TREND_TONE[row.trend]}>{row.trend}</Chip>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="py-2 text-gray-700">
                        {row.readThrough || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          {brief.coverage.length ? (
            <ul className="mt-3 space-y-2">
              {brief.coverage.map(c => (
                <li
                  key={c.category}
                  className="flex flex-wrap items-baseline gap-2 text-sm"
                >
                  <span className="font-medium text-gray-900">
                    {c.category}
                  </span>
                  {c.status ? (
                    <Chip tone={COVERAGE_TONE[c.status]}>{c.status}</Chip>
                  ) : null}
                  {c.note ? (
                    <span className="text-gray-600">{c.note}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
        </CollapsibleSection>

        <CollapsibleSection title="Performance health" nested>
          {brief.healthTrend ? (
            <div className="mb-2">
              <Chip tone={TREND_TONE[brief.healthTrend]}>
                {brief.healthTrend}
              </Chip>
            </div>
          ) : null}
          {brief.healthText ? <Prose>{brief.healthText}</Prose> : null}
          {brief.keyNumbers.length ? (
            <ul className="mt-3 grid gap-2 sm:grid-cols-2">
              {brief.keyNumbers.map(k => (
                <li
                  key={`${k.signal}-${k.reading}`}
                  className="rounded-md border border-[#e3e8f0] bg-white px-3 py-2"
                >
                  <div className="flex items-center gap-1 text-xs font-semibold text-gray-500">
                    {k.signal}
                    <DirectionIcon direction={k.direction} />
                  </div>
                  <div className="text-sm text-gray-900">{k.reading}</div>
                  {k.category ? (
                    <div
                      className="mt-1 text-[10px] font-semibold uppercase tracking-wide"
                      style={{ color: MUTED }}
                    >
                      {k.category}
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
          {brief.forwardIndicators ? (
            <Labelled label="Forward indicators">
              {brief.forwardIndicators}
            </Labelled>
          ) : null}
          {brief.growthPosture ? (
            <Labelled label="Growth posture">{brief.growthPosture}</Labelled>
          ) : null}
        </CollapsibleSection>
      </GroupCard>

      <GroupCard title="Watch-outs" Icon={AlertTriangle} id="keb-watch-outs">
        <CollapsibleSection title="What's changing" nested>
          <PointList points={brief.changes} empty="No material changes." />
        </CollapsibleSection>

        <CollapsibleSection title="Landmines" nested>
          {brief.landminePosture ? (
            <div className="mb-2">
              <Prose>{brief.landminePosture}</Prose>
            </div>
          ) : null}
          {brief.noMaterialRisks && !brief.landmines.length ? (
            <Prose>No material risks.</Prose>
          ) : (
            <PointList points={brief.landmines} empty="No landmines." />
          )}
        </CollapsibleSection>

        <CollapsibleSection title="Relationship implications" nested>
          {brief.motion || brief.urgency ? (
            <div className="mb-2 flex flex-wrap gap-2">
              {brief.motion ? (
                <Chip tone="bg-purple-50 text-[#ad46ff]">
                  {capitalize(brief.motion)}
                </Chip>
              ) : null}
              {brief.urgency ? (
                <Chip>{URGENCY_LABEL[brief.urgency] ?? brief.urgency}</Chip>
              ) : null}
            </div>
          ) : null}
          {brief.whatItMeans ? <Prose>{brief.whatItMeans}</Prose> : null}
          {brief.motionRationale ? (
            <Labelled label="Why this motion">{brief.motionRationale}</Labelled>
          ) : null}
          {brief.valueConcentration.length ? (
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-gray-700">
              {brief.valueConcentration.map(p => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          ) : null}
          {brief.caution ? (
            <Labelled label="Caution">{brief.caution}</Labelled>
          ) : null}
        </CollapsibleSection>
      </GroupCard>

      <GroupCard
        title="Recommended actions and owners"
        Icon={ListChecks}
        id="keb-playbook"
      >
        <MoveList moves={brief.playbook} numbered />
        {brief.dropped.length ? (
          <div className="mt-4">
            <h4
              className="mb-2 text-[11px] font-semibold uppercase tracking-wide"
              style={{ color: ACCENT }}
            >
              Considered and not recommended
            </h4>
            <PointList points={brief.dropped} dense hideChip />
          </div>
        ) : null}
      </GroupCard>

      <GroupCard title="Open questions" Icon={HelpCircle} id="keb-questions">
        <PointList
          points={brief.questions}
          numbered
          empty="No open questions."
        />
      </GroupCard>
    </div>
  );
};

export default KpiExecutiveBrief;
